import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import {
  X,
  Sun,
  Moon,
  LogOut,
  Trash2,
  Shield,
  HelpCircle,
  ExternalLink,
  Loader2,
  RotateCcw,
  Bug,
  Send,
  CheckCircle2,
  Crown,
  Phone,
  Gift,
  Share2,
  Copy,
  ChevronDown,
  BarChart3,
  Download,
} from 'lucide-react';
import { resetOnboarding } from '../onboarding/OnboardingWalkthrough';
import { useTheme } from '../../context/ThemeContext';
import { useAuth } from '../../context/AuthContext';
import { useApp } from '../../context/AppContext';
import { PremiumUpsellSheet } from '../premium/PremiumUpsellSheet';
import { supabase } from '../../lib/supabase';
import { hashPhone, normalizeToE164, getCountryFromPhone } from '../../utils/phoneHash';
import { saveLocalPhone } from '../../utils/localPhone';
import { shareText } from '../../utils/nativeShare';
import { COUNTRY_CODES } from '../../utils/countryCodes';
import { Capacitor } from '@capacitor/core';
import { getReferralCount, updateProfile, deleteImportedContacts, hasImportedContacts, exportUserData } from '../../services/supabaseService';
import { DOWNLOAD_URL, PRIVACY_URL, TERMS_URL, SUPPORT_URL } from '../../constants/links';

interface SettingsScreenProps {
  open: boolean;
  onClose: () => void;
}

export function SettingsScreen({ open, onClose }: SettingsScreenProps) {
  const { theme, toggleTheme } = useTheme();
  const { signOut, deleteAccount, refreshProfile } = useAuth();
  const { currentUser, isPremium } = useApp();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showUpsell, setShowUpsell] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showBugReport, setShowBugReport] = useState(false);
  const [bugCategory, setBugCategory] = useState<'bug' | 'feedback' | 'question'>('bug');
  const [bugMessage, setBugMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [showPhoneEdit, setShowPhoneEdit] = useState(false);
  const [phoneCountryCode, setPhoneCountryCode] = useState('971');
  const [phoneDigits, setPhoneDigits] = useState('');
  const [phoneSaving, setPhoneSaving] = useState(false);
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const [instagramHandle, setInstagramHandle] = useState(currentUser.instagramHandle ?? '');
  const [igSaving, setIgSaving] = useState(false);
  const [referralCount, setReferralCount] = useState(0);
  const [codeCopied, setCodeCopied] = useState(false);
  const [leaderboardVisible, setLeaderboardVisible] = useState(
    currentUser.leaderboardVisible !== false
  );
  const [lbSaving, setLbSaving] = useState(false);
  const [hasContacts, setHasContacts] = useState(false);
  const [clearingContacts, setClearingContacts] = useState(false);
  const [contactsCleared, setContactsCleared] = useState(false);
  const [exporting, setExporting] = useState(false);

  const handleExportData = async () => {
    setExporting(true);
    try {
      const data = await exportUserData(currentUser.id);
      const json = JSON.stringify(data, null, 2);
      const blob = new Blob([json], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `rostandfound-data-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.warn('[Settings] export failed:', err);
    } finally {
      setExporting(false);
    }
  };

  const handleClearContacts = async () => {
    setClearingContacts(true);
    try {
      await deleteImportedContacts(currentUser.id);
      setHasContacts(false);
      setContactsCleared(true);
    } catch (err) {
      console.warn('[Settings] clear contacts failed:', err);
    } finally {
      setClearingContacts(false);
    }
  };

  const handleToggleLeaderboard = async () => {
    const newValue = !leaderboardVisible;
    setLeaderboardVisible(newValue);
    setLbSaving(true);
    try {
      await updateProfile(currentUser.id, { leaderboardVisible: newValue });
      await refreshProfile();
    } catch (err) {
      console.warn('[Settings] leaderboard toggle failed:', err);
      setLeaderboardVisible(!newValue); // revert on error
    } finally {
      setLbSaving(false);
    }
  };

  useEffect(() => {
    if (open) {
      getReferralCount(currentUser.id).then(setReferralCount).catch(() => {});
      hasImportedContacts(currentUser.id).then(setHasContacts).catch(() => {});
      setContactsCleared(false);
    }
  }, [open, currentUser.id]);

  const handleSubmitBug = async () => {
    const trimmed = bugMessage.trim();
    if (!trimmed || submitting) return;
    setSubmitting(true);
    try {
      const deviceInfo = `${navigator.userAgent} | ${Capacitor.getPlatform()} | ${window.innerWidth}x${window.innerHeight}`;
      await supabase.from('support_tickets').insert({
        user_id: currentUser.id,
        category: bugCategory,
        message: trimmed,
        app_version: 'v1.0',
        device_info: deviceInfo,
      });
      setSubmitted(true);
      setBugMessage('');
      setTimeout(() => {
        setShowBugReport(false);
        setSubmitted(false);
      }, 2000);
    } catch (err) {
      console.warn('[Settings] bug report failed:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleSavePhone = async () => {
    setPhoneError(null);
    const digits = phoneDigits.replace(/\D/g, '');
    if (!digits || digits.length < 6 || digits.length > 15) {
      setPhoneError('Please enter a valid phone number (6-15 digits).');
      return;
    }
    const phoneValue = `+${phoneCountryCode}${digits}`;
    const e164 = normalizeToE164(phoneValue);
    if (!e164) {
      setPhoneError('Could not validate this phone number. Please check the format.');
      return;
    }
    setPhoneSaving(true);
    let phoneHash: string;
    try {
      phoneHash = await hashPhone(e164);
    } catch {
      setPhoneError('Failed to process phone number. Please try again.');
      setPhoneSaving(false);
      return;
    }
    saveLocalPhone(currentUser.id, e164);
    const { error: dbError } = await supabase
      .from('profiles')
      .update({ phone_hash: phoneHash })
      .eq('id', currentUser.id);
    if (dbError) { setPhoneError(dbError.message); setPhoneSaving(false); return; }
    await refreshProfile();
    setPhoneSaving(false);
    setShowPhoneEdit(false);
  };

  const handleSaveInstagram = async () => {
    const raw = instagramHandle.trim().replace(/^@/, '');
    if (raw === (currentUser.instagramHandle ?? '')) return;
    setIgSaving(true);
    try {
      await updateProfile(currentUser.id, { instagramHandle: raw || undefined });
      await refreshProfile();
    } catch (err) {
      console.warn('[Settings] IG save failed:', err);
    } finally {
      setIgSaving(false);
    }
  };

  if (!open) return null;

  const openUrl = (url: string) => {
    window.open(url, '_blank', 'noopener');
  };

  return createPortal(
    <div className="absolute inset-0 z-[100] bg-slate-900 flex flex-col">
      {/* Header */}
      <div
        className="flex items-center justify-between px-4 py-3 border-b border-slate-700/50"
        style={{ paddingTop: 'calc(var(--sat, 0px) + 12px)' }}
      >
        <h2 className="text-lg font-bold text-slate-100">Settings</h2>
        <button
          onClick={onClose}
          className="p-2 rounded-lg hover:bg-slate-800 text-slate-400"
        >
          <X size={20} />
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-6">
        {/* Subscription */}
        <Section title="Subscription">
          {isPremium ? (
            <SettingsRow
              icon={<Crown size={18} />}
              label="Premium Active"
              trailing={
                currentUser.tierExpiresAt ? (
                  <span className="text-xs text-slate-500">
                    Expires {new Date(currentUser.tierExpiresAt).toLocaleDateString()}
                  </span>
                ) : (
                  <span className="text-xs text-amber-400 font-medium">Active</span>
                )
              }
              onClick={() => {}}
            />
          ) : (
            <SettingsRow
              icon={<Crown size={18} />}
              label="Upgrade to Premium"
              trailing={<span className="text-xs text-amber-400 font-semibold">Unlock</span>}
              onClick={() => setShowUpsell(true)}
            />
          )}
        </Section>

        {/* Referrals */}
        <Section title="Referrals">
          <SettingsRow
            icon={<Copy size={18} />}
            label="Your Code"
            trailing={
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  navigator.clipboard.writeText(currentUser.handle).then(() => {
                    setCodeCopied(true);
                    setTimeout(() => setCodeCopied(false), 2000);
                  }).catch(() => {});
                }}
                className="text-xs text-accent font-mono active:opacity-70"
              >
                {codeCopied ? 'Copied!' : `@${currentUser.handle}`}
              </button>
            }
            onClick={() => {
              navigator.clipboard.writeText(currentUser.handle).then(() => {
                setCodeCopied(true);
                setTimeout(() => setCodeCopied(false), 2000);
              }).catch(() => {});
            }}
          />
          <SettingsRow
            icon={<Gift size={18} />}
            label="Friends Referred"
            trailing={
              <span className="text-xs text-slate-400 font-medium">
                {referralCount} {referralCount === 1 ? 'friend' : 'friends'}
              </span>
            }
            onClick={() => {}}
          />
          <SettingsRow
            icon={<Share2 size={18} />}
            label="Share Referral Link"
            onClick={async () => {
              const text = `Join me on Rost & Found! Use my code "${currentUser.handle}" when you sign up and we both benefit.\n\nDownload: ${DOWNLOAD_URL}\nhttps://rostandfound.app/?ref=${currentUser.handle}`;
              await shareText({ title: 'Rost & Found', text });
            }}
          />
        </Section>

        {/* Appearance */}
        <Section title="Appearance">
          <SettingsRow
            icon={theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
            label={theme === 'dark' ? 'Light Mode' : 'Dark Mode'}
            onClick={toggleTheme}
          />
          <SettingsRow
            icon={<RotateCcw size={18} />}
            label="Replay Onboarding"
            onClick={() => {
              resetOnboarding();
              onClose();
              window.dispatchEvent(new Event('replay-onboarding'));
            }}
          />
        </Section>

        {/* Support */}
        <Section title="Support">
          <SettingsRow
            icon={<Bug size={18} />}
            label="Report a Bug"
            onClick={() => setShowBugReport(true)}
          />
          <SettingsRow
            icon={<HelpCircle size={18} />}
            label="Help & FAQ"
            trailing={<ExternalLink size={14} className="text-slate-600" />}
            onClick={() => openUrl(SUPPORT_URL)}
          />
        </Section>

        {/* Legal */}
        <Section title="Legal">
          <SettingsRow
            icon={<Shield size={18} />}
            label="Terms of Service"
            trailing={<ExternalLink size={14} className="text-slate-600" />}
            onClick={() => openUrl(TERMS_URL)}
          />
          <SettingsRow
            icon={<Shield size={18} />}
            label="Privacy Policy"
            trailing={<ExternalLink size={14} className="text-slate-600" />}
            onClick={() => openUrl(PRIVACY_URL)}
          />
        </Section>

        {/* Privacy */}
        <Section title="Privacy">
          <div className="flex items-center gap-3 px-4 py-3.5">
            <span className="text-slate-400">
              <BarChart3 size={18} />
            </span>
            <span className="text-sm font-medium text-slate-200">Show on Leaderboard</span>
            <div className="ml-auto flex items-center gap-2">
              {lbSaving && <Loader2 size={14} className="animate-spin text-slate-500" />}
              <button
                onClick={handleToggleLeaderboard}
                disabled={lbSaving}
                className={`relative w-10 h-5.5 rounded-full transition-colors ${
                  leaderboardVisible ? 'bg-accent' : 'bg-slate-600'
                }`}
                style={{ height: '22px' }}
              >
                <span
                  className={`absolute top-0.5 left-0.5 w-4.5 h-4.5 bg-white rounded-full shadow transition-transform ${
                    leaderboardVisible ? 'translate-x-4.5' : 'translate-x-0'
                  }`}
                  style={{ width: '18px', height: '18px', transform: leaderboardVisible ? 'translateX(18px)' : 'translateX(0)' }}
                />
              </button>
            </div>
          </div>
          <SettingsRow
            icon={<Download size={18} />}
            label="Export My Data"
            trailing={
              exporting ? (
                <Loader2 size={14} className="animate-spin text-slate-500" />
              ) : null
            }
            onClick={handleExportData}
          />
          <SettingsRow
            icon={<Phone size={18} />}
            label="Clear Imported Contacts"
            trailing={
              clearingContacts ? (
                <Loader2 size={14} className="animate-spin text-slate-500" />
              ) : contactsCleared ? (
                <span className="text-xs text-green-400 font-medium">Cleared</span>
              ) : !hasContacts ? (
                <span className="text-xs text-slate-600">None stored</span>
              ) : null
            }
            onClick={hasContacts && !clearingContacts && !contactsCleared ? handleClearContacts : () => {}}
          />
        </Section>

        {/* Account */}
        <Section title="Account">
          <div className="flex items-center gap-3 px-4 py-3.5">
            <span className="text-slate-400">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
                <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
                <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" />
              </svg>
            </span>
            <span className="text-sm font-medium text-slate-200 shrink-0">Instagram</span>
            <input
              type="text"
              value={instagramHandle}
              onChange={(e) => setInstagramHandle(e.target.value.replace(/\s/g, ''))}
              onBlur={handleSaveInstagram}
              placeholder="@username"
              className="ml-auto w-36 text-right text-sm bg-transparent text-slate-300 placeholder-slate-600 outline-none"
            />
            {igSaving && <Loader2 size={14} className="animate-spin text-slate-500 shrink-0" />}
          </div>
          <SettingsRow
            icon={<Phone size={18} />}
            label="Phone Number"
            trailing={
              <span className="text-xs text-slate-500">
                {currentUser.phone || 'Not set'}
              </span>
            }
            onClick={() => {
              // Parse existing phone to split into country code + national
              if (currentUser.phone) {
                const country = getCountryFromPhone(currentUser.phone);
                const cc = COUNTRY_CODES.find((c) => c.code === country);
                if (cc) {
                  setPhoneCountryCode(cc.dial);
                  // Strip the country code prefix to show just national digits
                  const stripped = currentUser.phone.replace(`+${cc.dial}`, '');
                  setPhoneDigits(stripped);
                } else {
                  setPhoneCountryCode('971');
                  setPhoneDigits(currentUser.phone.replace(/^\+\d{1,3}/, ''));
                }
              } else {
                setPhoneCountryCode('971');
                setPhoneDigits('');
              }
              setPhoneError(null);
              setShowPhoneEdit(true);
            }}
          />
          <SettingsRow
            icon={<LogOut size={18} />}
            label="Sign Out"
            danger
            onClick={() => {
              signOut().catch((err) => console.warn('[Settings] signOut failed:', err));
            }}
          />
          <SettingsRow
            icon={<Trash2 size={18} />}
            label="Delete Account"
            danger
            subtle
            onClick={() => setShowDeleteConfirm(true)}
          />
        </Section>

        {/* Version */}
        <p className="text-center text-xs text-slate-600 pt-4">
          Rost & Found v1.0
        </p>
      </div>

      {/* Delete Account Confirmation */}
      {showDeleteConfirm && (
        <div className="absolute inset-0 z-[110] flex items-center justify-center bg-black/60 px-6">
          <div className="bg-slate-800 rounded-2xl p-5 w-full max-w-sm border border-slate-700">
            <h3 className="text-lg font-bold text-slate-100 mb-2">Delete Account?</h3>
            <p className="text-sm text-slate-400 mb-4">
              This will permanently delete your profile, all flights, friendships, and data.
              This action cannot be undone.
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                disabled={deleting}
                className="flex-1 py-2.5 rounded-lg bg-slate-700 text-slate-300 text-sm font-medium hover:bg-slate-600"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  setDeleting(true);
                  const result = await deleteAccount();
                  setDeleting(false);
                  if (result.error) {
                    console.warn('[Settings] deleteAccount failed:', result.error);
                  }
                  setShowDeleteConfirm(false);
                }}
                disabled={deleting}
                className="flex-1 py-2.5 rounded-lg bg-red-600 text-white text-sm font-medium hover:bg-red-700 disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {deleting ? (
                  <>
                    <Loader2 size={14} className="animate-spin" />
                    Deleting...
                  </>
                ) : (
                  'Delete'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Phone Edit Dialog */}
      {showPhoneEdit && (
        <div className="absolute inset-0 z-[110] flex items-center justify-center bg-black/60 px-6">
          <div className="bg-slate-800 rounded-2xl p-5 w-full max-w-sm border border-slate-700">
            <h3 className="text-lg font-bold text-slate-100 mb-2">Update Phone Number</h3>
            <p className="text-xs text-slate-400 mb-3">
              Used for contact matching so crew members can find you.
            </p>
            {phoneError && (
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2 text-red-400 text-xs mb-3">{phoneError}</div>
            )}
            <div className="flex gap-2">
              <div className="relative shrink-0">
                <select
                  value={phoneCountryCode}
                  onChange={(e) => setPhoneCountryCode(e.target.value)}
                  className="w-[100px] px-2.5 py-2.5 rounded-lg bg-slate-700 border border-slate-600 text-slate-100 text-sm focus:outline-none focus:ring-2 focus:ring-accent/50 focus:border-accent appearance-none"
                >
                  {COUNTRY_CODES.map((c) => (
                    <option key={c.code} value={c.dial} className="bg-slate-700 text-slate-100">
                      {c.flag} +{c.dial}
                    </option>
                  ))}
                </select>
                <ChevronDown
                  size={14}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none"
                />
              </div>
              <input
                type="tel"
                autoComplete="tel-national"
                value={phoneDigits}
                onChange={(e) => setPhoneDigits(e.target.value.replace(/[^\d\s]/g, ''))}
                placeholder="50 1234567"
                className="flex-1 px-3 py-2.5 rounded-lg bg-slate-700 border border-slate-600 text-slate-100 placeholder-slate-500 text-sm focus:outline-none focus:ring-2 focus:ring-accent/50 focus:border-accent"
              />
            </div>
            <div className="flex gap-2 mt-3">
              <button
                onClick={() => setShowPhoneEdit(false)}
                disabled={phoneSaving}
                className="flex-1 py-2.5 rounded-lg bg-slate-700 text-slate-300 text-sm font-medium hover:bg-slate-600"
              >
                Cancel
              </button>
              <button
                onClick={handleSavePhone}
                disabled={phoneSaving}
                className="flex-1 py-2.5 rounded-lg bg-accent text-white text-sm font-medium disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {phoneSaving ? <Loader2 size={14} className="animate-spin" /> : null}
                {phoneSaving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      <PremiumUpsellSheet open={showUpsell} onClose={() => setShowUpsell(false)} />

      {/* Bug Report Form */}
      {showBugReport && (
        <div className="absolute inset-0 z-[110] flex items-center justify-center bg-black/60 px-6">
          <div className="bg-slate-800 rounded-2xl p-5 w-full max-w-sm border border-slate-700">
            {submitted ? (
              <div className="flex flex-col items-center gap-3 py-4">
                <CheckCircle2 size={40} className="text-green-400" />
                <p className="text-sm font-semibold text-slate-100">Thanks for your report!</p>
                <p className="text-xs text-slate-400 text-center">We'll look into it.</p>
              </div>
            ) : (
              <>
                <h3 className="text-lg font-bold text-slate-100 mb-3">Report a Bug</h3>
                {/* Category picker */}
                <div className="flex gap-2 mb-3">
                  {(['bug', 'feedback', 'question'] as const).map((cat) => (
                    <button
                      key={cat}
                      onClick={() => setBugCategory(cat)}
                      className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                        bugCategory === cat
                          ? 'bg-accent text-white'
                          : 'bg-slate-700 text-slate-400'
                      }`}
                    >
                      {cat === 'bug' ? 'Bug' : cat === 'feedback' ? 'Feedback' : 'Question'}
                    </button>
                  ))}
                </div>
                {/* Message */}
                <textarea
                  value={bugMessage}
                  onChange={(e) => setBugMessage(e.target.value)}
                  placeholder="Describe the issue or share your feedback..."
                  rows={4}
                  className="w-full bg-slate-700 text-slate-100 placeholder-slate-500 rounded-xl px-3 py-2.5 text-sm resize-none outline-none focus:ring-2 focus:ring-accent/40"
                />
                <p className="text-[11px] text-slate-600 mt-1.5 mb-3">
                  Device info and your account will be attached automatically.
                </p>
                {/* Actions */}
                <div className="flex gap-2">
                  <button
                    onClick={() => { setShowBugReport(false); setBugMessage(''); }}
                    className="flex-1 py-2.5 rounded-lg bg-slate-700 text-slate-300 text-sm font-medium hover:bg-slate-600"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSubmitBug}
                    disabled={!bugMessage.trim() || submitting}
                    className="flex-1 py-2.5 rounded-lg bg-accent text-white text-sm font-medium disabled:opacity-50 flex items-center justify-center gap-2 active:scale-95 transition-transform"
                  >
                    {submitting ? (
                      <>
                        <Loader2 size={14} className="animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send size={14} />
                        Send
                      </>
                    )}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>,
    document.getElementById('app-shell') || document.body
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2 px-1">
        {title}
      </h3>
      <div className="bg-slate-800 rounded-xl border border-slate-700/50 divide-y divide-slate-700/50">
        {children}
      </div>
    </div>
  );
}

function SettingsRow({
  icon,
  label,
  trailing,
  danger,
  subtle,
  onClick,
}: {
  icon: React.ReactNode;
  label: string;
  trailing?: React.ReactNode;
  danger?: boolean;
  subtle?: boolean;
  onClick: () => void;
}) {
  const textColor = danger
    ? subtle
      ? 'text-slate-500'
      : 'text-red-400'
    : 'text-slate-200';
  const iconColor = danger
    ? subtle
      ? 'text-slate-600'
      : 'text-red-400'
    : 'text-slate-400';

  return (
    <button
      onClick={onClick}
      className={`w-full flex items-center gap-3 px-4 py-3.5 hover:bg-slate-700/50 transition-colors ${
        subtle ? 'text-xs' : 'text-sm'
      }`}
    >
      <span className={iconColor}>{icon}</span>
      <span className={`font-medium ${textColor}`}>{label}</span>
      {trailing && <span className="ml-auto">{trailing}</span>}
    </button>
  );
}
